import React, { useState, useEffect, useRef } from 'react';
import { collection, query, where, getDocs, doc, getDoc, updateDoc, deleteDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { ChevronUpIcon, ChevronDownIcon, XMarkIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { getStorage, ref, getDownloadURL } from "firebase/storage";
import { DocumentIcon } from '@heroicons/react/24/outline';
import { documentRequirements } from '../customerPortal/documentTracker';

// Component accepts either:
// - prequalId: for broker prequalification applications (uses prequalificationMetadata)
// - applicationId + businessIndex: for borrower loan applications (uses documentMetadata)
const PrequalificationOcrReview = ({ prequalId, applicationId, businessIndex, onOcrDataUpdate }) => {
  const [documents, setDocuments] = useState([]);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [isOcrReviewExpanded, setIsOcrReviewExpanded] = useState(true);
  const [selectedDocumentFileUrl, setSelectedDocumentFileUrl] = useState('');
  const [isFullScreenMode, setIsFullScreenMode] = useState(false);
  const [confirmedFields, setConfirmedFields] = useState({});

  // Determine which mode we're in
  const isLoanApplication = !!applicationId && !prequalId;
  const effectiveId = prequalId || applicationId;
  const iframeRef = useRef(null);
  const [pdfObjectUrl, setPdfObjectUrl] = useState(null);
  const [lastFetchTimestamp, setLastFetchTimestamp] = useState(0);
  const [newAddBackCategory, setNewAddBackCategory] = useState('');
  const [newAddBackAmount, setNewAddBackAmount] = useState('');
  const [showDebugInfo, setShowDebugInfo] = useState(false);

  // Define default OCR structure - these fields will always be shown
  const defaultOcrFields = {
    gross_receipts_or_sales: '',
    cost_of_goods_sold: '',
    ordinary_business_income: '',
    compensation_of_officers: '',
    depreciation: '',
    interest: '',
    amortization: '',
    new_officer_compensation: 75000, // Add default value for new officer compensation
    other_deductions: [], // Ensure this is initialized as an empty array
  };

  // Standard fields that should always be displayed (in order)
  const standardOcrFields = [
    { field: 'gross_receipts_or_sales', label: 'Gross Receipts / Sales' },
    { field: 'cost_of_goods_sold', label: 'Cost of Goods Sold' },
    { field: 'ordinary_business_income', label: 'Ordinary Business Income' },
    { field: 'compensation_of_officers', label: 'Compensation of Officers' },
    { field: 'depreciation', label: 'Depreciation' },
    { field: 'interest', label: 'Interest' },
    { field: 'amortization', label: 'Amortization' },
  ];

  useEffect(() => {
    const fetchDocuments = async () => {
      try {
        console.log("Starting fetchDocuments with effectiveId:", effectiveId, "isLoanApplication:", isLoanApplication);

        let metadata = [];

        if (isLoanApplication) {
          // For loan applications, fetch from documentMetadata collection
          const metadataRef = collection(db, 'documentMetadata');
          let metadataQuery;

          if (businessIndex !== undefined && businessIndex !== null) {
            // Filter by businessIndex for specific business documents
            metadataQuery = query(metadataRef,
              where('loanApplicationId', '==', effectiveId),
              where('businessIndex', '==', businessIndex)
            );
          } else {
            // Get all documents for this loan
            metadataQuery = query(metadataRef,
              where('loanApplicationId', '==', effectiveId)
            );
          }

          const metadataSnap = await getDocs(metadataQuery);
          metadata = metadataSnap.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));

          // Also fetch documents without businessIndex (could be business tax returns)
          if (businessIndex !== undefined && businessIndex !== null) {
            const generalDocsQuery = query(metadataRef,
              where('loanApplicationId', '==', effectiveId)
            );
            const generalDocsSnap = await getDocs(generalDocsQuery);
            const generalDocs = generalDocsSnap.docs
              .map(doc => ({ id: doc.id, ...doc.data() }))
              .filter(doc =>
                (doc.businessIndex === undefined || doc.businessIndex === null || doc.businessIndex === businessIndex) &&
                !doc.ownerId && // Exclude personal guarantor documents
                (doc.documentName === 'Business Tax Returns' || doc.documentName === 'PnL' || doc.documentName === 'Balance Sheet')
              );

            // Merge without duplicates
            const existingIds = new Set(metadata.map(m => m.id));
            generalDocs.forEach(doc => {
              if (!existingIds.has(doc.id)) {
                metadata.push(doc);
              }
            });
          }

          console.log("Found loan application documents:", metadata);
        } else {
          // For prequalification applications, fetch from prequalificationMetadata
          const metadataRef = collection(db, 'prequalificationMetadata');
          const metadataQuery = query(metadataRef,
            where('applicationId', '==', effectiveId)
          );
          const metadataSnap = await getDocs(metadataQuery);
          metadata = metadataSnap.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          console.log("Found prequalification metadata documents:", metadata);
        }

        // Then get OCR data (same collection for both types, but query by appropriate ID)
        const ocrMetadataRef = collection(db, 'OCRMetadata');
        const ocrQuery = query(ocrMetadataRef,
          where('applicationId', '==', effectiveId)
        );
        const ocrQuerySnapshot = await getDocs(ocrQuery);

        console.log("OCR data query results count:", ocrQuerySnapshot.size);
        
        // Create a map of OCR data by documentId
        const ocrDataMap = {};
        ocrQuerySnapshot.forEach((docSnap) => {
          const data = docSnap.data();
          // Use structuredData as ocrResult if available
          const ocrResultData = data.structuredData || data.ocrResult || {}; 
          
          // Create a modified data object with structuredData mapped to ocrResult if needed
          const processedData = {
            ...data,
            ocrResult: ocrResultData,
            firestoreId: docSnap.id
          };
          
          // Try to match by multiple possible ID fields
          if (data.documentId) {
            ocrDataMap[data.documentId] = processedData;
            console.log(`Found OCR data for document ID ${data.documentId}:`, processedData.ocrResult);
          }
          
          // Also store by fileName for backup matching
          if (data.fileName) {
            ocrDataMap[`file_${data.fileName}`] = processedData;
          }
        });

        // Process and combine metadata with OCR data
        const processedDocs = metadata.map(doc => {
          const ocrData = ocrDataMap[doc.id] || ocrDataMap[`file_${doc.fileName}`];
          
          return {
            id: doc.id,
            fileName: doc.fileName,
            fileUrl: doc.fileUrl,
            documentName: doc.documentName,
            label: doc.label,
            ocrResult: ocrData?.ocrResult || defaultOcrFields,
            confirmedFields: ocrData?.confirmedFields || {},
            ocrFirestoreId: ocrData?.firestoreId,
            documentId: doc.id,
            originalDoc: doc
          };
        });

        console.log("Final processed documents:", processedDocs);
        setDocuments(processedDocs);

        // Select first document if none selected
        if (!selectedDocument && processedDocs.length > 0) {
          handleDocumentSelect(processedDocs[0]);
        }

      } catch (error) {
        console.error('Error in fetchDocuments:', error);
      }
    };

    if (effectiveId) {
      fetchDocuments();
    }
  }, [effectiveId, isLoanApplication, businessIndex, lastFetchTimestamp]);

  const handleDocumentSelect = (document) => {
    if (!document) return;

    console.log("Selecting document:", document);
    console.log("Document OCR result:", document.ocrResult);
    console.log("Document structured data:", document.structuredData);
    console.log("Document confirmed fields:", document.confirmedFields);
    console.log("Document label:", document.label);

    // Ensure the document has all required fields
    const documentWithDefaults = {
      ...document,
      ocrResult: document.ocrResult || document.structuredData || defaultOcrFields,
    };

    setSelectedDocument(documentWithDefaults);
    setSelectedDocumentFileUrl(document.fileUrl);
    setConfirmedFields(document.confirmedFields || {});
  };

  const handleOcrInputChange = (e, field) => {
    if (selectedDocument && selectedDocument.ocrResult) {
      let newValue = e.target.value;
      
      if (['compensation_of_officers', 'gross_receipts_or_sales', 'ordinary_business_income', 'cost_of_goods_sold'].includes(field) || field.startsWith('amount_')) {
        newValue = newValue === '' ? null : Number(newValue);
      }

      setSelectedDocument(prev => ({
        ...prev,
        ocrResult: {
          ...prev.ocrResult,
          [field]: newValue,
        }
      }));
      setConfirmedFields(prev => ({ ...prev, [field]: false }));
    }
  };

  const handleSaveOcrChanges = async () => {
    if (!selectedDocument) return;

    try {
      console.log("Saving OCR changes for document:", selectedDocument);
      
      // CRITICAL: Ensure we have the correct document ID and label
      const documentId = selectedDocument.id;
      const documentLabel = selectedDocument.label;
      const fileName = selectedDocument.fileName;
      
      console.log("DOCUMENT ID TO BE SAVED:", documentId);
      console.log("DOCUMENT LABEL TO BE SAVED:", documentLabel);
      console.log("DOCUMENT FILENAME TO BE SAVED:", fileName);
      
      if (!documentId) {
        console.error("ERROR: Document has no ID!", selectedDocument);
        alert('Error: Document has no ID. Cannot save OCR data.');
        return;
      }
      
      // Ensure other_deductions is an array and process numeric fields
      const ocrDataToSave = { ...selectedDocument.ocrResult };

      const numericFieldKeys = [
        'gross_receipts_or_sales', 'cost_of_goods_sold', 'ordinary_business_income',
        'compensation_of_officers', 'depreciation', 'interest', 'amortization',
        'new_officer_compensation' // This is typically handled by input, but good to include
      ];

      for (const key of Object.keys(ocrDataToSave)) {
        if (numericFieldKeys.includes(key)) {
          let value = ocrDataToSave[key];
          if (typeof value === 'string') {
            const cleanedValue = value.replace(/,/g, ''); // Remove commas
            if (cleanedValue === '') {
              ocrDataToSave[key] = null; // Empty string becomes null
            } else {
              const num = Number(cleanedValue);
              ocrDataToSave[key] = isNaN(num) ? null : num; // Convert to number, or null if NaN
            }
          } else if (typeof value === 'number' && isNaN(value)) {
            ocrDataToSave[key] = null; // Convert existing NaN to null
          } else if (value === undefined) { // Handle potential undefined values
             ocrDataToSave[key] = null;
          }
          // If it's already a valid number or null, it remains unchanged
        }
      }

      // Process 'amount' in other_deductions to ensure they are numbers or null
      if (Array.isArray(ocrDataToSave.other_deductions)) {
        ocrDataToSave.other_deductions = ocrDataToSave.other_deductions.map(deduction => {
          let amount = deduction.amount;
          if (typeof amount === 'string') {
            const cleanedAmount = amount.replace(/,/g, ''); // Remove commas
            if (cleanedAmount === '') {
              amount = null; // Empty string becomes null
            } else {
              const numAmount = Number(cleanedAmount);
              amount = isNaN(numAmount) ? null : numAmount; // Convert to number, or null if NaN
            }
          } else if (typeof amount === 'number' && isNaN(amount)) {
            amount = null; // Convert existing NaN to null
          } else if (amount === undefined) { // Handle potential undefined values
            amount = null;
          }
          // If it's already a valid number or null, it remains unchanged
          return { ...deduction, amount: amount };
        });
      } else {
        ocrDataToSave.other_deductions = []; // Ensure it's an array if it wasn't (e.g. from defaultOcrFields)
      }
      
      const sanitizedOcrResult = ocrDataToSave;
      
      // Prepare OCR metadata
      const ocrMetadata = {
        ocrResult: sanitizedOcrResult, // This now contains proper numbers or nulls
        structuredData: sanitizedOcrResult, // Ensure structuredData also gets the numeric conversion
        confirmedFields: confirmedFields,
        fileName: fileName,
        documentName: selectedDocument.documentName,
        label: documentLabel,
        applicationId: prequalId,
        documentId: documentId,
        updatedAt: serverTimestamp()
      };
      
      console.log("OCR METADATA BEING SAVED:", ocrMetadata);

      let savedDocId;
      
      // If we already have an ocrFirestoreId, update that document
      if (selectedDocument.ocrFirestoreId) {
        savedDocId = selectedDocument.ocrFirestoreId;
        console.log(`Updating existing OCR document with ID: ${savedDocId}`);
        const ocrDocRef = doc(db, 'OCRMetadata', savedDocId);
        await updateDoc(ocrDocRef, ocrMetadata);
      } else {
        // Check if there are any existing OCR documents for this documentId and applicationId
        const ocrMetadataRef = collection(db, 'OCRMetadata');
        const q = query(
          ocrMetadataRef, 
          where('documentId', '==', documentId), 
          where('applicationId', '==', prequalId)
        );
        const existingOcrDocs = await getDocs(q);
        
        if (!existingOcrDocs.empty) {
          // Update the first existing document
          savedDocId = existingOcrDocs.docs[0].id;
          console.log(`Found and updating existing OCR document with ID: ${savedDocId}`);
          const ocrDocRef = doc(db, 'OCRMetadata', savedDocId);
          await updateDoc(ocrDocRef, ocrMetadata);
        } else {
          // Try to find by filename as a fallback
          const filenameQuery = query(
            ocrMetadataRef, 
            where('fileName', '==', fileName), 
            where('applicationId', '==', prequalId)
          );
          const filenameQueryDocs = await getDocs(filenameQuery);
          
          if (!filenameQueryDocs.empty) {
            savedDocId = filenameQueryDocs.docs[0].id;
            console.log(`Found and updating existing OCR document by filename with ID: ${savedDocId}`);
            const ocrDocRef = doc(db, 'OCRMetadata', savedDocId);
            await updateDoc(ocrDocRef, ocrMetadata);
          } else {
            // Create new OCR document
            const newDocRef = doc(collection(db, 'OCRMetadata'));
            savedDocId = newDocRef.id;
            
            console.log(`Creating new OCR document with ID: ${savedDocId}`);
            
            await setDoc(newDocRef, {
              ...ocrMetadata,
              createdAt: serverTimestamp()
            });
            
            console.log(`OCR document created with ID: ${savedDocId}`);
          }
        }
      }

      // Update the documents array with the saved data
      setDocuments(prevDocs => 
        prevDocs.map(doc => 
          doc.id === documentId 
            ? {
                ...doc,
                ocrResult: {...sanitizedOcrResult},
                confirmedFields: {...confirmedFields},
                ocrFirestoreId: savedDocId
              }
            : doc
        )
      );
      
      // Update the selected document with the saved data and Firestore ID
      setSelectedDocument(prev => ({
        ...prev,
        ocrResult: {...sanitizedOcrResult},
        confirmedFields: {...confirmedFields},
        ocrFirestoreId: savedDocId
      }));

      console.log(`OCR changes saved successfully for document ID: ${documentId} with Firestore ID: ${savedDocId}`);
      alert('OCR changes saved successfully!');
      
      // Trigger a refresh to ensure data consistency
      // setLastFetchTimestamp(Date.now());
    } catch (error) {
      console.error('Error saving OCR changes:', error);
      alert(`Failed to save OCR changes: ${error.message || error.code || 'Unknown error'}. Please try again.`);
    }
  };

  // Add function to confirm all fields at once
  const confirmAllFields = () => {
    if (!selectedDocument || !selectedDocument.ocrResult) return;
    
    // Create an object with all fields marked as 'unchanged'
    const allFields = {};
    
    // Add all regular fields
    Object.keys(selectedDocument.ocrResult).forEach(field => {
      if (field !== 'id' && field !== 'documentId' && field !== 'firestoreId' && 
          field !== 'createdAt' && field !== 'updatedAt') {
        allFields[field] = 'unchanged';
      }
    });
    
    // Specially handle other_deductions
    if (selectedDocument.ocrResult.other_deductions && 
        Array.isArray(selectedDocument.ocrResult.other_deductions) && 
        selectedDocument.ocrResult.other_deductions.length > 0) {
      // If there are add-backs present, mark them as unchanged
      allFields.other_deductions = 'unchanged';
    }
    
    // Set all fields as confirmed
    setConfirmedFields(allFields);
    console.log("All fields confirmed:", allFields);
  };

  const handleConfirmField = (field, status) => {
    // Just update the local state, don't try to save to Firestore yet
    const newConfirmedFields = { 
      ...confirmedFields, 
      [field]: status 
    };
    
    console.log(`Setting confirmed status for ${field} to ${status}`);
    setConfirmedFields(newConfirmedFields);
  };

  const handleOtherDeductionChange = (index, key, value) => {
    if (selectedDocument && selectedDocument.ocrResult) {
      const newOtherDeductions = [...(selectedDocument.ocrResult.other_deductions || [])]; // Ensure array if undefined
      
      let processedValue = value;
      if (key === 'amount') {
        // If input is empty string, store null. Otherwise, convert to number.
        processedValue = value === '' ? null : Number(value);
      }

      newOtherDeductions[index] = {
        ...newOtherDeductions[index],
        [key]: processedValue
      };
      setSelectedDocument(prev => ({
        ...prev,
        ocrResult: {
          ...prev.ocrResult,
          other_deductions: newOtherDeductions
        }
      }));
      // Mark other_deductions as changed when we edit an item
      setConfirmedFields(prev => ({ ...prev, other_deductions: 'changed' }));
    }
  };

  const handleAddNewAddBack = () => {
    if (!newAddBackCategory || !newAddBackAmount || isNaN(Number(newAddBackAmount))) {
      alert('Please enter a valid category name and amount');
      return;
    }

    if (selectedDocument && selectedDocument.ocrResult) {
      // Initialize other_deductions if it doesn't exist or isn't an array
      const currentDeductions = Array.isArray(selectedDocument.ocrResult.other_deductions) 
        ? [...selectedDocument.ocrResult.other_deductions] 
        : [];
      
      // Add the new add-back
      const updatedDeductions = [
        ...currentDeductions,
        {
          category: newAddBackCategory,
          amount: Number(newAddBackAmount)
        }
      ];
      
      // Update the selected document
      setSelectedDocument(prev => ({
        ...prev,
        ocrResult: {
          ...prev.ocrResult,
          other_deductions: updatedDeductions
        }
      }));
      
      // Mark as changed - always set to 'changed' when adding a new item
      setConfirmedFields(prev => ({ ...prev, other_deductions: 'changed' }));
      
      // Clear the input fields
      setNewAddBackCategory('');
      setNewAddBackAmount('');
    }
  };

  const handleRemoveAddBack = (index) => {
    if (selectedDocument && selectedDocument.ocrResult && Array.isArray(selectedDocument.ocrResult.other_deductions)) {
      const updatedDeductions = [...selectedDocument.ocrResult.other_deductions];
      updatedDeductions.splice(index, 1);
      
      setSelectedDocument(prev => ({
        ...prev,
        ocrResult: {
          ...prev.ocrResult,
          other_deductions: updatedDeductions
        }
      }));
      
      // Always mark as changed when removing an item
      setConfirmedFields(prev => ({ ...prev, other_deductions: 'changed' }));
    }
  };

  // Add a function to format field labels nicely
  const formatFieldLabel = (field) => {
    // Convert camelCase or snake_case to Title Case with spaces
    const formatted = field
      .replace(/_/g, ' ')
      .replace(/([A-Z])/g, ' $1')
      .replace(/^./, str => str.toUpperCase());
    
    return formatted;
  };

  const renderField = (label, field, value) => {
    // Add debug logging at the start
    console.log(`Rendering field ${field} with type: ${typeof value}, value:`, value);
    console.log(`Confirmed status for ${field}:`, confirmedFields[field]);

    const isNumeric = ['compensation_of_officers', 'gross_receipts_or_sales', 'ordinary_business_income', 'cost_of_goods_sold', 'interest', 'depreciation', 'amortization'].includes(field) || field.startsWith('amount_');
    
    if (field === 'other_deductions') {
      return (
        <div key={field} className="mb-4">
          <div className="flex justify-between items-center">
            <label className="block text-sm font-medium text-gray-700">Add-Backs</label>
            <div className="ml-2 flex space-x-2">
              <button
                onClick={() => handleConfirmField('other_deductions', 'changed')}
                className={`p-2 rounded-full transition-colors duration-200 ${
                  confirmedFields['other_deductions'] === 'changed' 
                    ? 'bg-blue-500 text-white' 
                    : 'bg-white text-blue-300 border border-blue-200 hover:bg-blue-50 hover:border-blue-300'
                }`}
                title="Confirm with changes"
              >
                {confirmedFields['other_deductions'] === 'changed' ? (
                  <CheckCircleIcon className="h-6 w-6" />
                ) : (
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <circle cx="12" cy="12" r="10" strokeWidth="2" />
                  </svg>
                )}
              </button>
              <button
                onClick={() => handleConfirmField('other_deductions', 'unchanged')}
                className={`p-2 rounded-full transition-colors duration-200 ${
                  confirmedFields['other_deductions'] === 'unchanged' 
                    ? 'bg-green-500 text-white' 
                    : 'bg-white text-green-300 border border-green-200 hover:bg-green-50 hover:border-green-300'
                }`}
                title="Confirm without changes"
              >
                {confirmedFields['other_deductions'] === 'unchanged' ? (
                  <CheckCircleIcon className="h-6 w-6" />
                ) : (
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <circle cx="12" cy="12" r="10" strokeWidth="2" />
                  </svg>
                )}
              </button>
            </div>
          </div>
          
          {/* Existing add-backs */}
          <div className="mt-1 border rounded-md p-2">
            {Array.isArray(value) && value.length > 0 ? (
              value.map((deduction, index) => {
                // Prepare deduction.amount for type="number" input
                let deductionAmountForInput;
                const currentDeductionAmount = deduction.amount;
                if (currentDeductionAmount === null || currentDeductionAmount === undefined) {
                    deductionAmountForInput = '';
                } else if (typeof currentDeductionAmount === 'number') {
                    deductionAmountForInput = currentDeductionAmount.toString();
                } else if (typeof currentDeductionAmount === 'string') {
                    const cleanedAmount = currentDeductionAmount.replace(/,/g, '');
                    if (cleanedAmount === '' || (isNaN(Number(cleanedAmount)) && cleanedAmount !== '') || !isFinite(Number(cleanedAmount))) {
                        deductionAmountForInput = cleanedAmount === '' ? '' : '';
                    } else {
                        deductionAmountForInput = cleanedAmount;
                    }
                } else {
                    deductionAmountForInput = ''; 
                }

                return (
                  <div key={index} className="flex justify-between items-center mb-2">
                    <div className="flex-grow">
                      <input
                        type="text"
                        value={deduction.category === null || deduction.category === undefined ? '' : String(deduction.category)}
                        onChange={(e) => handleOtherDeductionChange(index, 'category', e.target.value)}
                        className="w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      />
                    </div>
                    <div className="flex items-center ml-2">
                      <input
                        type="number"
                        value={deductionAmountForInput}
                        onChange={(e) => handleOtherDeductionChange(index, 'amount', e.target.value)}
                        className="w-24 rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                      />
                      <button
                        onClick={() => handleRemoveAddBack(index)}
                        className="ml-2 text-red-500 hover:text-red-700"
                        title="Remove add-back"
                      >
                        <XMarkIcon className="h-5 w-5" />
                      </button>
                    </div>
                  </div>
                );
              })
            ) : (
              <p className="text-gray-500 text-center py-2">No add-backs added yet</p>
            )}
          </div>
          
          {/* Add new add-back form */}
          <div className="mt-3 flex items-end space-x-2">
            <div className="flex-grow">
              <label className="block text-sm font-medium text-gray-700">Category</label>
              <input
                type="text"
                value={newAddBackCategory}
                onChange={(e) => setNewAddBackCategory(e.target.value)}
                placeholder="e.g., MEALS, TRAVEL"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700">Amount</label>
              <input
                type="number"
                value={newAddBackAmount}
                onChange={(e) => setNewAddBackAmount(e.target.value)}
                placeholder="0"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              />
            </div>
            <button
              onClick={handleAddNewAddBack}
              className="py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Add
            </button>
          </div>
        </div>
      );
    }

    // Make sure we're displaying the correct value from the selected document
    let finalValueForInput;

    if (value === null || value === undefined) {
      finalValueForInput = '';
    } else {
      if (isNumeric) {
        if (typeof value === 'number') {
          finalValueForInput = value.toString();
        } else if (typeof value === 'string') {
          const cleaned = value.replace(/,/g, '');
          if (cleaned === '' || (isNaN(Number(cleaned)) && cleaned !== '') || !isFinite(Number(cleaned))) {
            finalValueForInput = cleaned === '' ? '' : '';
          } else {
            finalValueForInput = cleaned;
          }
        } else {
          // If isNumeric, but value is neither number nor string (e.g. boolean), display empty
          finalValueForInput = '';
        }
      } else {
        // For non-numeric fields, simply convert to string
        finalValueForInput = String(value);
      }
    }

    return (
      <div key={field} className="mb-4 flex items-center">
        <div className="flex-grow">
          <label className="block text-sm font-medium text-gray-700">{label}</label>
          <input
            type={isNumeric ? "number" : "text"}
            value={finalValueForInput}
            onChange={(e) => handleOcrInputChange(e, field)}
            className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
          />
        </div>
        <div className="ml-2 flex space-x-2">
          <button
            onClick={() => handleConfirmField(field, 'changed')}
            className={`p-2 rounded-full transition-colors duration-200 ${
              confirmedFields[field] === 'changed' 
                ? 'bg-blue-500 text-white' 
                : 'bg-white text-blue-300 border border-blue-200 hover:bg-blue-50 hover:border-blue-300'
            }`}
            title="Confirm with changes"
          >
            {confirmedFields[field] === 'changed' ? (
              <CheckCircleIcon className="h-6 w-6" />
            ) : (
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <circle cx="12" cy="12" r="10" strokeWidth="2" />
              </svg>
            )}
          </button>
          <button
            onClick={() => handleConfirmField(field, 'unchanged')}
            className={`p-2 rounded-full transition-colors duration-200 ${
              confirmedFields[field] === 'unchanged' 
                ? 'bg-green-500 text-white' 
                : 'bg-white text-green-300 border border-green-200 hover:bg-green-50 hover:border-green-300'
            }`}
            title="Confirm without changes"
          >
            {confirmedFields[field] === 'unchanged' ? (
              <CheckCircleIcon className="h-6 w-6" />
            ) : (
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <circle cx="12" cy="12" r="10" strokeWidth="2" />
              </svg>
            )}
          </button>
        </div>
      </div>
    );
  };

  const renderOcrReviewContent = () => {
    let finalNocInputValue = '75000'; // Default value as string

    if (selectedDocument && selectedDocument.ocrResult) {
      let rawNocValue = selectedDocument.ocrResult.new_officer_compensation;
      let processedNocValueForDefaultCheck;

      if (typeof rawNocValue === 'string') {
        const cleanedNoc = rawNocValue.replace(/,/g, '');
        const numericEquivalent = Number(cleanedNoc);
        if (!isNaN(numericEquivalent) && isFinite(numericEquivalent)) {
          processedNocValueForDefaultCheck = numericEquivalent;
        } else {
          processedNocValueForDefaultCheck = undefined; // Will trigger default
        }
      } else {
        processedNocValueForDefaultCheck = rawNocValue; // Could be number, null, undefined, etc.
      }
      
      // Apply default if processedNocValueForDefaultCheck is falsy (0, null, undefined, NaN), otherwise use its string representation.
      // If value is 0, it will use 75000 due to `||` operator. If 0 should be preserved and not default, this logic would need adjustment.
      finalNocInputValue = (processedNocValueForDefaultCheck || 75000).toString();
    }

    return (
      <div className="flex h-full">
        <div className={`${isFullScreenMode ? 'w-[35%]' : 'w-1/2'} p-6 overflow-y-auto`}>
          <h4 className="text-lg font-medium mb-4">Documents</h4>
          <div className="space-y-2">
            {documents.length > 0 ? (
              documents.map(doc => (
                <div
                  key={doc.id}
                  className={`cursor-pointer p-4 rounded-lg border ${
                    selectedDocument?.id === doc.id ? 'bg-blue-50 border-blue-500' : 'border-gray-200 hover:bg-gray-50'
                  }`}
                  onClick={() => handleDocumentSelect(doc)}
                >
                  <div className="flex items-center">
                    <DocumentIcon className="h-5 w-5 text-gray-400 mr-2" />
                    <div>
                      <p className="font-medium text-gray-900">{doc.fileName}</p>
                      <p className="text-sm text-gray-500">{doc.documentName || doc.label}</p>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <p className="text-gray-500 text-center py-4">No documents available</p>
            )}
          </div>

          {selectedDocument && (
            <>
              <h4 className="text-lg font-medium mb-2 mt-6">Edit OCR Results</h4>
              
              {/* Add the "Confirm All Fields" button */}
              <div className="mb-4">
                <button
                  onClick={confirmAllFields}
                  className="w-full inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                >
                  Confirm All Fields
                </button>
              </div>
              
              <div className="mb-4 flex items-center">
                <div className="flex-grow">
                  <label className="block text-sm font-medium text-gray-700">New Officer Compensation</label>
                  <input
                    type="number"
                    value={finalNocInputValue}
                    onChange={(e) => handleOcrInputChange(e, 'new_officer_compensation')}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  />
                </div>
                <div className="ml-2 flex space-x-2">
                  <button
                    onClick={() => handleConfirmField('new_officer_compensation', 'changed')}
                    className={`p-2 rounded-full transition-colors duration-200 ${
                      confirmedFields['new_officer_compensation'] === 'changed' 
                        ? 'bg-blue-500 text-white' 
                        : 'bg-white text-blue-300 border border-blue-200 hover:bg-blue-50 hover:border-blue-300'
                    }`}
                    title="Confirm with changes"
                  >
                    {confirmedFields['new_officer_compensation'] === 'changed' ? (
                      <CheckCircleIcon className="h-6 w-6" />
                    ) : (
                      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <circle cx="12" cy="12" r="10" strokeWidth="2" />
                      </svg>
                    )}
                  </button>
                  <button
                    onClick={() => handleConfirmField('new_officer_compensation', 'unchanged')}
                    className={`p-2 rounded-full transition-colors duration-200 ${
                      confirmedFields['new_officer_compensation'] === 'unchanged' 
                        ? 'bg-green-500 text-white' 
                        : 'bg-white text-green-300 border border-green-200 hover:bg-green-50 hover:border-green-300'
                    }`}
                    title="Confirm without changes"
                  >
                    {confirmedFields['new_officer_compensation'] === 'unchanged' ? (
                      <CheckCircleIcon className="h-6 w-6" />
                    ) : (
                      <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <circle cx="12" cy="12" r="10" strokeWidth="2" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>
              {/* Render all standard OCR fields - always show them even if empty */}
              {standardOcrFields.map(({ field, label }) => {
                const value = selectedDocument.ocrResult?.[field] ?? '';
                return renderField(label, field, value);
              })}

              {/* Render Other Deductions / Add-Backs */}
              {renderField('Other Deductions', 'other_deductions', selectedDocument.ocrResult?.other_deductions || [])}
              <button
                onClick={handleSaveOcrChanges}
                className="mt-4 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Save Changes
              </button>
            </>
          )}
        </div>

        <div className={`${isFullScreenMode ? 'w-[65%]' : 'w-1/2'} border-l border-gray-200`}>
          {selectedDocumentFileUrl ? (
            <embed
              src={`${selectedDocumentFileUrl}#toolbar=0&navpanes=0&scrollbar=0`}
              type="application/pdf"
              width="100%"
              height="100%"
            />
          ) : (
            <div className="flex items-center justify-center h-full">
              <p className="text-gray-500">Select a document to view</p>
            </div>
          )}
        </div>
      </div>
    );
  };

  useEffect(() => {
    if (iframeRef.current && selectedDocumentFileUrl) {
      console.log("Reloading iframe with new URL:", selectedDocumentFileUrl);
      iframeRef.current.src = `${selectedDocumentFileUrl}#toolbar=0&navpanes=0&scrollbar=0&t=${Date.now()}`;
    }
  }, [selectedDocumentFileUrl]);

  useEffect(() => {
    console.log("selectedDocumentFileUrl changed:", selectedDocumentFileUrl);
  }, [selectedDocumentFileUrl]);

  console.log("Rendering PrequalificationOcrReview with state:", {
    documents,
    selectedDocument,
    selectedDocumentFileUrl,
    isOcrReviewExpanded,
    isFullScreenMode,
    confirmedFields
  });

  // Add a separate refresh button function that users can manually trigger
  const refreshDocuments = () => {
    console.log("Manually refreshing documents...");
    setLastFetchTimestamp(Date.now());
  };

  // Add a useEffect to log confirmedFields changes for debugging
  useEffect(() => {
    console.log("confirmedFields updated:", confirmedFields);
  }, [confirmedFields]);

  // Add this useEffect to log the selected document whenever it changes
  useEffect(() => {
    if (selectedDocument) {
      console.log("Selected document changed:", selectedDocument);
      console.log("Selected document OCR result:", selectedDocument.ocrResult);
    }
  }, [selectedDocument]);

  const toggleDebugInfo = () => {
    setShowDebugInfo(!showDebugInfo);
  };

  return (
    <>
      <div className="bg-white shadow-lg rounded-lg overflow-hidden mb-8">
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
          <div>
            <h3 className="text-base font-semibold leading-7 text-gray-900">Prequalification OCR Review</h3>
            <p className="mt-1 max-w-2xl text-sm leading-6 text-gray-500">Review and edit OCR results for prequalification documents.</p>
          </div>
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setIsFullScreenMode(!isFullScreenMode)}
              className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-md text-indigo-700 bg-indigo-100 hover:bg-indigo-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Full Screen
            </button>
            <button
              onClick={() => setIsOcrReviewExpanded(!isOcrReviewExpanded)}
              className="text-gray-500 hover:text-gray-700 focus:outline-none"
            >
              {isOcrReviewExpanded ? (
                <ChevronUpIcon className="h-6 w-6" />
              ) : (
                <ChevronDownIcon className="h-6 w-6" />
              )}
            </button>
          </div>
        </div>
        {isOcrReviewExpanded && (
          <div className="h-[calc(100vh-200px)]">
            {renderOcrReviewContent()}
          </div>
        )}
      </div>
      {isFullScreenMode && (
        <div className="fixed inset-0 z-50 bg-white shadow-lg rounded-lg overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
            <div>
              <h3 className="text-base font-semibold leading-7 text-gray-900">Prequalification OCR Review</h3>
              <p className="mt-1 max-w-2xl text-sm leading-6 text-gray-500">Review and edit OCR results for prequalification documents.</p>
            </div>
            <div className="flex items-center space-x-2">
              <button
                onClick={confirmAllFields}
                className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 mr-2"
              >
                Confirm All Fields
              </button>
              <button
                onClick={handleSaveOcrChanges}
                className="inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                Save Changes
              </button>
              <button
                onClick={() => setIsFullScreenMode(false)}
                className="text-gray-500 hover:text-gray-700 focus:outline-none"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
          </div>
          <div className="h-[calc(100vh-64px)]">
            {renderOcrReviewContent()}
          </div>
        </div>
      )}
      <button
        onClick={refreshDocuments}
        className="mt-4 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
      >
        Refresh Documents
      </button>
      {selectedDocument && (
        <>
          <div className="mt-4 flex justify-between">
            <button
              onClick={toggleDebugInfo}
              className="text-gray-500 text-sm hover:text-gray-700"
            >
              {showDebugInfo ? 'Hide Debug Info' : 'Show Debug Info'}
            </button>
          </div>
          
          {showDebugInfo && (
            <div className="mt-2 p-4 bg-gray-100 rounded-md">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Document Debug Info</h4>
              <pre className="text-xs overflow-auto max-h-64">
                {JSON.stringify(selectedDocument, null, 2)}
              </pre>
            </div>
          )}
        </>
      )}
    </>
  );
};

export default PrequalificationOcrReview;